// src/context/AuthContext.js
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  linkWithCredential,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { sendVerificationEmail, friendlyAuthError } from '../utils/authEmail';
import { explainGoogleError } from '../utils/googleAuth';
import { googleCredentialFromError, readGoogleRedirectResult, startGoogleSignIn } from '../utils/googleSignIn';
import { buildNewUserProfile, classifySignedInUser } from '../utils/userProfile';

const AuthContext = createContext(null);

const MISSING_GOOGLE_EMAIL = 'Google did not share an email address. Use email and password instead.';

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  // Set when the verification email could not be sent at signup (shown on /verify-email)
  const [verificationSendError, setVerificationSendError] = useState('');
  // Google user who still needs to choose customer or carrier and finish the form.
  const [pendingSetup, setPendingSetup] = useState(null);
  // Set when a Google popup or redirect finishes, so the open auth page can route.
  const [googleAuthResult, setGoogleAuthResult] = useState(null);
  // Profile we just wrote, in case auth state resolves before that write is visible.
  const profileRef = useRef(null);
  // Google credential waiting to be linked after the user signs in with a password.
  const pendingLinkRef = useRef(null);

  const clearGoogleAuthResult = useCallback(() => setGoogleAuthResult(null), []);

  const commitUser = (next) => {
    profileRef.current = next;
    setPendingSetup(null);
    setUser(next);
  };

  const applyOutcome = async (firebaseUser, snap) => {
    const outcome = classifySignedInUser({
      firebaseUser,
      profile: snap && snap.exists() ? snap.data() : null,
      keptProfile: profileRef.current,
    });

    if (outcome.kind === 'missingEmail') {
      profileRef.current = null;
      setPendingSetup(null);
      setUser(null);
      try { await signOut(auth); } catch (_) { /* already signed out */ }
      return outcome;
    }

    if (outcome.kind === 'existing') {
      commitUser(outcome.user);
      return outcome;
    }

    if (outcome.kind === 'needsSetup') {
      // Do not treat them as an app user until users/{uid} exists.
      if (!(profileRef.current && profileRef.current.uid === firebaseUser.uid)) {
        setUser(null);
      }
      setPendingSetup(outcome.pending);
      return outcome;
    }

    // Email/password sign-up can reach auth state before the profile write.
    setPendingSetup(null);
    if (profileRef.current && profileRef.current.uid === firebaseUser.uid) {
      setUser(profileRef.current);
      return { kind: 'existing', user: profileRef.current };
    }
    setUser(outcome.user);
    return outcome;
  };

  const loadProfile = async (firebaseUser) => {
    const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (snap.exists() && firebaseUser.emailVerified && !snap.data().emailVerified) {
      try {
        await updateDoc(doc(db, 'users', firebaseUser.uid), { emailVerified: true });
      } catch (syncErr) {
        console.warn('[AuthContext] emailVerified sync failed (non-fatal):', syncErr.message);
      }
    }
    return applyOutcome(firebaseUser, snap);
  };

  const noteGoogleLinkError = (err) => {
    if (err?.code !== 'auth/account-exists-with-different-credential') return;
    const pending = googleCredentialFromError(err);
    if (pending.credential) pendingLinkRef.current = pending;
  };

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          await loadProfile(firebaseUser);
        } catch (loadErr) {
          console.warn('[AuthContext] profile load failed:', loadErr.message);
          setUser({ uid: firebaseUser.uid, email: firebaseUser.email, emailVerified: firebaseUser.emailVerified });
        }
      } else {
        profileRef.current = null;
        setPendingSetup(null);
        setUser(null);
      }
      setLoading(false);
    });
    return unsub;
    // loadProfile is stable for this effect: it only uses refs and setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Finish a Google redirect if the popup was blocked on the previous page.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await readGoogleRedirectResult();
        if (!active || !result?.user) return;
        const outcome = await loadProfile(result.user);
        if (!active) return;
        if (outcome.kind === 'missingEmail') {
          setError(MISSING_GOOGLE_EMAIL);
          return;
        }
        if (outcome.kind === 'existing' || outcome.kind === 'needsSetup') {
          setGoogleAuthResult(outcome);
        }
      } catch (err) {
        if (!active) return;
        noteGoogleLinkError(err);
        setError(explainGoogleError(err) || friendlyAuthError(err));
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkPendingGoogle = async (firebaseUser) => {
    const pending = pendingLinkRef.current;
    if (!pending?.credential || !firebaseUser) return;
    pendingLinkRef.current = null;
    const pendingEmail = (pending.email || '').toLowerCase();
    const signedInEmail = (firebaseUser.email || '').toLowerCase();
    if (pendingEmail && signedInEmail && pendingEmail !== signedInEmail) return;
    try {
      await linkWithCredential(firebaseUser, pending.credential);
    } catch (err) {
      console.warn('[Auth] Google link failed:', err.code, err.message);
    }
  };

  const sendVerificationIfNeeded = async (firebaseUser) => {
    if (firebaseUser.emailVerified) return true;
    try {
      await sendVerificationEmail(firebaseUser);
      setVerificationSendError('');
      return true;
    } catch (verifyErr) {
      console.error('sendEmailVerification failed:', verifyErr.code, verifyErr.message);
      setVerificationSendError(friendlyAuthError(verifyErr));
      return false;
    }
  };

  // SIGN UP (email and password — unchanged flow)
  const signup = async (email, password, type, name, extra = {}) => {
    setError('');
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });

      const profile = buildNewUserProfile({ name, email, type, extra });
      await setDoc(doc(db, 'users', cred.user.uid), profile);

      const verificationSent = await sendVerificationIfNeeded(cred.user);
      commitUser({ uid: cred.user.uid, email, ...profile, emailVerified: cred.user.emailVerified });
      return { success: true, verificationSent, emailVerified: cred.user.emailVerified };
    } catch (err) {
      const msg = friendlyError(err.code);
      setError(msg);
      return { success: false, error: msg };
    }
  };

  // LOG IN
  const login = async (email, password) => {
    setError('');
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await linkPendingGoogle(cred.user);
      // Reload to ensure emailVerified is fresh from the server
      await cred.user.reload();
      const snap = await getDoc(doc(db, 'users', cred.user.uid));
      const profile = snap.exists() ? snap.data() : {};
      // Firebase Auth is authoritative for emailVerified (Firestore copy can be stale)
      const next = { uid: cred.user.uid, email, ...profile, emailVerified: cred.user.emailVerified };
      commitUser(next);
      return { success: true, type: profile.type, emailVerified: cred.user.emailVerified };
    } catch (err) {
      const msg = friendlyError(err.code);
      setError(msg);
      return { success: false, error: msg };
    }
  };

  // GOOGLE — popup, then redirect if the popup is blocked.
  // An existing users/{uid} doc signs them in. A first-time Google user is left
  // in pendingSetup so the sign-up form can write the same profile as email sign-up.
  const signInWithGoogle = async () => {
    setError('');
    try {
      const started = await startGoogleSignIn();
      if (started.redirecting) return { success: true, redirecting: true };
      if (!started.user) {
        const msg = friendlyAuthError('auth/popup-closed-by-user');
        setError(msg);
        return { success: false, error: msg };
      }
      const outcome = await loadProfile(started.user);
      if (outcome.kind === 'missingEmail') {
        setError(MISSING_GOOGLE_EMAIL);
        return { success: false, error: MISSING_GOOGLE_EMAIL };
      }
      if (outcome.kind === 'existing' || outcome.kind === 'needsSetup') {
        setGoogleAuthResult(outcome);
      }
      return { success: true, ...outcome };
    } catch (err) {
      noteGoogleLinkError(err);
      const msg = explainGoogleError(err) || friendlyAuthError(err);
      setError(msg);
      return { success: false, error: msg };
    }
  };

  // Create users/{uid} for a Google user after they finish the same form as email sign-up.
  const completeGoogleSignup = async (type, name, extra = {}) => {
    setError('');
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      const msg = 'Google sign-in was not completed. Try again.';
      setError(msg);
      return { success: false, error: msg };
    }
    if (type !== 'customer' && type !== 'provider') {
      const msg = 'Choose customer or carrier to create the account.';
      setError(msg);
      return { success: false, error: msg };
    }
    try {
      const existing = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (existing.exists()) {
        const profile = existing.data();
        const next = { uid: firebaseUser.uid, email: firebaseUser.email, ...profile, emailVerified: firebaseUser.emailVerified };
        commitUser(next);
        return { success: true, existing: true, type: profile.type, emailVerified: firebaseUser.emailVerified, verificationSent: true };
      }

      const displayName = (name || firebaseUser.displayName || '').trim();
      if (!displayName) {
        const msg = 'Please enter your name.';
        setError(msg);
        return { success: false, error: msg };
      }
      try {
        await updateProfile(firebaseUser, { displayName });
      } catch (nameErr) {
        console.warn('[Auth] display name update failed (non-fatal):', nameErr.message);
      }

      const profile = buildNewUserProfile({
        name: displayName,
        email: firebaseUser.email,
        type,
        extra,
      });
      await setDoc(doc(db, 'users', firebaseUser.uid), profile);

      const verificationSent = await sendVerificationIfNeeded(firebaseUser);
      commitUser({ uid: firebaseUser.uid, email: firebaseUser.email, ...profile, emailVerified: firebaseUser.emailVerified });
      return { success: true, verificationSent, emailVerified: firebaseUser.emailVerified, type };
    } catch (err) {
      const msg = friendlyError(err.code);
      setError(msg);
      return { success: false, error: msg };
    }
  };

  // LOG OUT
  const logout = async () => {
    profileRef.current = null;
    pendingLinkRef.current = null;
    setGoogleAuthResult(null);
    setPendingSetup(null);
    await signOut(auth);
    setUser(null);
  };

  // Update user profile in Firestore
  const updateUser = async (data) => {
    if (!user) return;
    await setDoc(doc(db, 'users', user.uid), data, { merge: true });
    setUser((prev) => {
      const next = { ...prev, ...data };
      profileRef.current = next;
      return next;
    });
  };

  // Re-fetch the Firestore user document and sync to context
  const refreshUser = async () => {
    if (!auth.currentUser) return;
    const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (snap.exists()) {
      // Keep emailVerified from Firebase Auth - dropping it made verified users look unverified
      commitUser({ uid: auth.currentUser.uid, email: auth.currentUser.email, ...snap.data(), emailVerified: auth.currentUser.emailVerified });
    }
  };

  // Reload Firebase auth user object, force-refresh the ID token so Firestore rules
  // immediately see the new email_verified claim, then sync into React context.
  const refreshEmailVerified = async () => {
    if (!auth.currentUser) return false;
    await auth.currentUser.reload();
    const verified = auth.currentUser.emailVerified;
    if (verified) {
      await auth.currentUser.getIdToken(true); // force-refresh: without this, Firestore rules
                                               // still see the old token claim for up to 1 hour
      try {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), { emailVerified: true });
      } catch (syncErr) {
        console.warn('[AuthContext] emailVerified sync failed (non-fatal):', syncErr.message);
      }
    }
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, emailVerified: verified };
      profileRef.current = next;
      return next;
    });
    return verified;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login,
      signup,
      logout,
      updateUser,
      refreshUser,
      refreshEmailVerified,
      verificationSendError,
      setVerificationSendError,
      signInWithGoogle,
      completeGoogleSignup,
      pendingSetup,
      googleAuthResult,
      clearGoogleAuthResult,
    }}>
      {loading
        ? (
          <div role="status" aria-live="polite" style={{ padding: '4rem 1rem' }}>
            <span style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}>Loading...</span>
          </div>
        )
        : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Turn Firebase error codes into friendly messages (shared helper)
const friendlyError = (code) => friendlyAuthError(code);
